import DOMPurify from "dompurify";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

import { splitName } from "common/splitName/splitName";
import { Button } from "components/button/Button";
import { Icon } from "components/icon/Icon";
import Persona from "components/persona/Persona";
import { Thread } from "models/inbox/inbox";

import "./ThreadPreview.scss";

type Props = {
  isSubscribing: boolean;
  thread: Thread;
  threadLink: string;
  handleThreadListAction: (action: string) => void;
};

export default function ThreadPreview({
  isSubscribing,
  thread,
  threadLink,
  handleThreadListAction
}: Props) {
  const [showReadMoreBtn, setShowReadMoreBtn] = useState(false);
  const [threadCollapsed, setThreadCollapsed] = useState(true);

  const messageBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      messageBodyRef.current &&
      messageBodyRef.current.scrollHeight > messageBodyRef.current.clientHeight
    ) {
      setShowReadMoreBtn(true);
    }
  }, []);

  const createMarkup = () => {
    return { __html: DOMPurify.sanitize(thread.lastMessage.body) };
  };

  const sender = thread.lastMessage.formatedFrom;
  const { firstName, lastName } = splitName(sender);

  const attachmentIcon = thread.lastMessage.attachments &&
    thread.lastMessage.attachments.length > 0 && (
      <div className="text-right pb-2">
        <Icon id={thread.threadId} name="paperclip" size="lg"></Icon>
      </div>
    );

  const timestamp = format(new Date(thread.lastMessage.createdAt), "HH:mm");

  const date = format(new Date(thread.lastMessage.createdAt), "d LLLL yyyy", {
    locale: sv
  });

  return (
    <div className={`threadPreview ${thread.unread && "threadPreviewUnread"}`}>
      <div className="d-flex">
        <Persona
          firstName={firstName}
          lastName={lastName}
          className="mr-4 d-sm-none"
        />
        <Link to={threadLink} className="threadPreviewBody">
          <div className="d-flex flex-wrap justify-content-between w-100">
            <div className="flex-1-1">
              <div className="sender text-nowrap">
                <div className="mr-2">Från: </div>
                <div>{thread.lastMessage.formatedFrom}</div>
              </div>

              <div className="sender text-nowrap">
                <div className="mr-2">Till: </div>
                <div className="d-flex flex-wrap">
                  {thread.subscribers
                    .filter(
                      (subscriber) => subscriber.id !== thread.lastMessage.from
                    )
                    .map((subscriber, i, arr) => (
                      <span className="mr-2" key={i}>
                        {`${subscriber.givenName} ${subscriber.surName}`}
                        {i !== arr.length - 1 && ", "}
                      </span>
                    ))}

                  <span>
                    {thread.subscribers.length > 5 && (
                      <> + {thread.subscribers.length - 5} andra mottagare</>
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="my-3 pl-4">
              {attachmentIcon}
              <time className="text-gray">
                {date} &#8212; {timestamp}
              </time>
            </div>
          </div>

          <h5 className="my-4">
            {thread.unread && "● "}
            {thread.title}
          </h5>

          <div
            ref={messageBodyRef}
            className={`messageBody ${threadCollapsed ? "collapsed" : ""}`}
            dangerouslySetInnerHTML={createMarkup()}
          ></div>
        </Link>
      </div>

      {showReadMoreBtn && (
        <div className="readMore">
          <Button
            size="sm"
            className="readMoreBtn"
            onClick={() => {
              setThreadCollapsed(!threadCollapsed);
            }}
          >
            {threadCollapsed ? (
              <span className="fw-bold">
                Läs mer <Icon name="arrow-down" className="ml-2" />
              </span>
            ) : (
              <span className="fw-bold">
                Läs mindre <Icon name="arrow-up" className="ml-2" />
              </span>
            )}
          </Button>
        </div>
      )}

      <div className="actions">
        {!thread.unread && isSubscribing && (
          <Button
            variant="outline"
            size="sm"
            className={"ml-3"}
            onClick={() => {
              if (thread.unread) {
                return;
              }
              handleThreadListAction("unread");
            }}
          >
            Markera som oläst
          </Button>
        )}
      </div>
    </div>
  );
}
